import React from 'react';

interface DownloadAuditBannerProps {
  showing: boolean;
  timestamp: string | null;
  action: string | null;
  onDismiss: () => void;
}

/**
 * A fixed-position popup banner that confirms a download has been recorded.
 * Renders at the top of the viewport with a timestamp.
 */
export function DownloadAuditBanner({ showing, timestamp, action, onDismiss }: DownloadAuditBannerProps) {
  if (!showing) return null;

  const formattedTime = timestamp
    ? new Date(timestamp).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : 'now';

  return (
    <div style={styles.overlay}>
      <div style={styles.banner}>
        <div style={styles.iconCol}>
          <span style={styles.icon}>📥</span>
        </div>
        <div style={styles.textCol}>
          <div style={styles.title}>Download Recorded</div>
          <div style={styles.detail}>
            <strong>{action}</strong> — {formattedTime}
          </div>
          <div style={styles.note}>This download has been logged for security audit purposes.</div>
        </div>
        <button style={styles.closeBtn} onClick={onDismiss} aria-label="Dismiss">✕</button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 99998,
    maxWidth: '440px',
    width: 'calc(100% - 32px)',
  },
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: '#1e293b',
    color: '#fff',
    borderRadius: '12px',
    boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
    animation: 'slideDown 0.3s ease-out',
  },
  iconCol: { flexShrink: 0, paddingTop: '2px' },
  icon: { fontSize: '1.4rem' },
  textCol: { flex: 1 },
  title: { fontWeight: 700, fontSize: '0.9rem', marginBottom: '2px' },
  detail: { fontSize: '0.78rem', opacity: 0.9, marginBottom: '4px' },
  note: { fontSize: '0.68rem', opacity: 0.6 },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.6)',
    fontSize: '1rem',
    cursor: 'pointer',
    padding: '4px',
    lineHeight: 1,
    flexShrink: 0,
  },
};
